import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Upload, Download, FileText, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Move, Check, FileStack, Loader2 } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UploadDocument() {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const [filePath, setFilePath] = useState('');
  const [pdfUrl, setPdfUrl] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [qrPosition, setQrPosition] = useState({ x: 420, y: 700 });
  const [qrSize, setQrSize] = useState(80);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPages, setSelectedPages] = useState([1]);
  const [numPages, setNumPages] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(false);
  
  // Drag state - use refs for performance
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, qrX: 0, qrY: 0 });
  
  const [documentId, setDocumentId] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [folderName, setFolderName] = useState('');
  const [folders, setFolders] = useState([]);
  const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
  const [validityStatus, setValidityStatus] = useState('Valid');
  const [finalizing, setFinalizing] = useState(false);
  
  const qrRef = useRef(null);
  const containerRef = useRef(null);

  // Fixed PDF display dimensions
  const PDF_DISPLAY_WIDTH = 550;
  const PDF_DISPLAY_HEIGHT = 780;

  useEffect(() => {
    loadFolders();
  }, []);

  const loadFolders = async () => {
    try {
      const response = await axios.get(`${API}/folders`);
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
    } else {
      toast.error('Please select a valid PDF file');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setVerificationId(response.data.verification_id);
      setFilePath(response.data.file_path);
      setPdfUrl(response.data.pdf_url);
      setQrCode(response.data.qr_code);
      setDocumentId(`DOC-${Date.now()}`);
      setPdfLoading(true);
      setPdfError(false);
      setStep(2);
      toast.success('PDF uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  // Simplified drag handling using mouse position directly
  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    dragStartRef.current = {
      x: clientX,
      y: clientY,
      qrX: qrPosition.x,
      qrY: qrPosition.y
    };
    
    setIsDragging(true);
  }, [qrPosition]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const deltaX = clientX - dragStartRef.current.x;
    const deltaY = clientY - dragStartRef.current.y;
    
    let newX = dragStartRef.current.qrX + deltaX;
    let newY = dragStartRef.current.qrY + deltaY;
    
    // Constrain to PDF bounds
    const maxX = PDF_DISPLAY_WIDTH - qrSize;
    const maxY = PDF_DISPLAY_HEIGHT - qrSize;
    
    newX = Math.max(0, Math.min(newX, maxX));
    newY = Math.max(0, Math.min(newY, maxY));
    
    setQrPosition({ x: Math.round(newX), y: Math.round(newY) });
  }, [isDragging, qrSize]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      const handleMove = (e) => handleDragMove(e);
      const handleEnd = () => handleDragEnd();
      
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
      
      return () => {
        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  const handleXChange = (value) => {
    const numValue = parseInt(value) || 0;
    const maxX = PDF_DISPLAY_WIDTH - qrSize;
    setQrPosition(prev => ({ ...prev, x: Math.max(0, Math.min(numValue, maxX)) }));
  };

  const handleYChange = (value) => {
    const numValue = parseInt(value) || 0;
    const maxY = PDF_DISPLAY_HEIGHT - qrSize;
    setQrPosition(prev => ({ ...prev, y: Math.max(0, Math.min(numValue, maxY)) }));
  };

  const handleSizeChange = (value) => {
    const numValue = parseInt(value) || 5;
    const newSize = Math.max(5, Math.min(200, numValue));
    setQrSize(newSize);
    
    // Adjust position if QR goes out of bounds
    const maxX = PDF_DISPLAY_WIDTH - newSize;
    const maxY = PDF_DISPLAY_HEIGHT - newSize;
    setQrPosition(prev => ({
      x: Math.min(prev.x, maxX),
      y: Math.min(prev.y, maxY)
    }));
  };

  const togglePageSelection = (pageNum) => {
    setSelectedPages(prev => {
      if (prev.includes(pageNum)) {
        if (prev.length === 1) {
          toast.error('At least one page must be selected');
          return prev;
        }
        return prev.filter(p => p !== pageNum);
      }
      return [...prev, pageNum].sort((a, b) => a - b);
    });
  };

  const selectAllPages = () => {
    if (numPages) {
      setSelectedPages(Array.from({ length: numPages }, (_, i) => i + 1));
    }
  };

  const handleProceedToMetadata = () => {
    if (selectedPages.length === 0) {
      toast.error('Please select at least one page for QR placement');
      return;
    }
    setStep(3);
  };

  const handleFinalize = async () => {
    if (!documentId || !documentName || !folderName) {
      toast.error('Please fill all required fields');
      return;
    }

    setFinalizing(true);
    const formData = new FormData();
    formData.append('verification_id', verificationId);
    formData.append('file_path', filePath);
    formData.append('qr_code', qrCode);
    formData.append('document_id', documentId);
    formData.append('document_name', documentName);
    formData.append('folder_name', folderName);
    formData.append('issue_date', issueDate);
    formData.append('validity_status', validityStatus);
    formData.append('qr_x', qrPosition.x);
    formData.append('qr_y', qrPosition.y);
    formData.append('qr_size', qrSize);
    formData.append('qr_pages', selectedPages.join(','));

    try {
      await axios.post(`${API}/documents/finalize`, formData);
      toast.success('Document generated successfully!');
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate document');
    } finally {
      setFinalizing(false);
    }
  };

  const handleDownload = () => {
    window.open(`${API}/documents/${verificationId}/pdf`, '_blank');
  };

  const handleReset = () => {
    setStep(1);
    setFile(null);
    setVerificationId('');
    setFilePath('');
    setPdfUrl('');
    setQrCode('');
    setQrPosition({ x: 420, y: 700 });
    setQrSize(80);
    setCurrentPage(1);
    setSelectedPages([1]);
    setNumPages(null);
    setPdfLoading(true);
    setPdfError(false);
    setDocumentId('');
    setDocumentName('');
    setFolderName('');
    setIssueDate(new Date().toISOString().split('T')[0]);
    setValidityStatus('Valid');
  };

  const onDocumentLoadSuccess = useCallback(({ numPages: totalPages }) => {
    setNumPages(totalPages);
    setCurrentPage(1);
    setSelectedPages([1]);
    setPdfLoading(false);
    setPdfError(false);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    setPdfLoading(false);
    setPdfError(true);
  }, []);

  // Memoize the PDF file object to prevent re-renders
  const pdfFile = useMemo(() => {
    if (!pdfUrl) return null;
    return `${BACKEND_URL}${pdfUrl}`;
  }, [pdfUrl]);

  const isPageSelected = selectedPages.includes(currentPage);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Step Indicator */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 overflow-x-auto">
        <div className="flex items-center justify-between min-w-[480px] sm:min-w-0 max-w-3xl mx-auto">
          {[
            { num: 1, label: 'Upload' },
            { num: 2, label: 'Position QR' },
            { num: 3, label: 'Metadata' },
            { num: 4, label: 'Complete' }
          ].map((s, idx) => (
            <div key={s.num} className="flex items-center">
              <div className={`flex items-center gap-1.5 sm:gap-2 ${step >= s.num ? 'text-slate-900' : 'text-slate-400'}`}>
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-medium text-sm ${
                  step > s.num ? 'bg-green-600 text-white' :
                  step === s.num ? 'bg-slate-900 text-white' : 'bg-slate-200'
                }`}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className="font-medium text-xs sm:text-sm whitespace-nowrap">{s.label}</span>
              </div>
              {idx < 3 && <div className="w-6 sm:w-10 lg:w-14 h-0.5 bg-slate-200 mx-2"></div>}
            </div>
          ))}
        </div>
      </div>

      {/* Step 1: Upload PDF */}
      {step === 1 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
            <div className="text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Upload className="w-7 h-7 sm:w-8 sm:h-8 text-slate-600" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Upload Base Document</h2>
              <p className="text-sm sm:text-base text-slate-600">Upload a PDF document without QR code</p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <Label htmlFor="pdf-upload" className="text-slate-700 font-medium">Select PDF File</Label>
              <Input
                id="pdf-upload"
                type="file"
                accept="application/pdf"
                onChange={handleFileSelect}
                className="h-10 sm:h-11"
                data-testid="pdf-upload-input"
              />
              {file && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-md">
                  <FileText className="w-5 h-5 text-slate-600 flex-shrink-0" />
                  <span className="text-sm text-slate-900 font-medium truncate flex-1">{file.name}</span>
                  <span className="text-xs text-slate-500 flex-shrink-0">{(file.size / 1024).toFixed(1)} KB</span>
                </div>
              )}
            </div>

            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="w-full h-10 sm:h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              data-testid="upload-pdf-button"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : 'Upload & Generate QR Code'}
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Position QR Code */}
      {step === 2 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
          <div className="space-y-4 sm:space-y-6">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Position & Resize QR Code</h2>
              <p className="text-sm sm:text-base text-slate-600">Drag the QR code or use controls below</p>
            </div>

            {/* Page Navigation */}
            {numPages && numPages > 1 && (
              <div className="flex items-center justify-center gap-3 sm:gap-4 p-3 sm:p-4 bg-slate-50 rounded-lg">
                <Button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage <= 1}
                  variant="outline"
                  size="sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="text-center">
                  <span className="text-sm font-medium">Page {currentPage} of {numPages}</span>
                  {isPageSelected && <p className="text-xs text-green-600 font-medium">QR will be placed here</p>}
                </div>
                <Button
                  onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
                  disabled={currentPage >= numPages}
                  variant="outline"
                  size="sm"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Page Selection Panel */}
            {numPages && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileStack className="w-4 h-4 text-amber-700" />
                  <span className="text-sm font-semibold text-amber-900">Page Selection for QR Placement</span>
                </div>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button onClick={() => setSelectedPages([currentPage])} variant="outline" size="sm" className="text-xs h-8">
                    Current Page Only
                  </Button>
                  {numPages > 1 && (
                    <>
                      <Button onClick={selectAllPages} variant="outline" size="sm" className="text-xs h-8">All Pages</Button>
                      <Button onClick={() => setSelectedPages([1])} variant="outline" size="sm" className="text-xs h-8">First Page Only</Button>
                      <Button onClick={() => setSelectedPages([numPages])} variant="outline" size="sm" className="text-xs h-8">Last Page Only</Button>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => togglePageSelection(pageNum)}
                      className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg border-2 font-medium text-sm transition-all ${
                        selectedPages.includes(pageNum)
                          ? 'bg-amber-700 border-amber-700 text-white'
                          : 'bg-white border-slate-300 text-slate-700 hover:border-amber-400'
                      } ${currentPage === pageNum ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}
                      data-testid={`page-select-${pageNum}`}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                <p className="text-xs text-amber-800 mt-3">
                  <strong>Selected:</strong> {selectedPages.length === 0 ? 'None' : 
                    selectedPages.length === numPages ? 'All pages' : 
                    `Page${selectedPages.length > 1 ? 's' : ''} ${selectedPages.join(', ')}`}
                </p>
              </div>
            )}

            {/* Position Controls */}
            <div className="bg-slate-50 rounded-lg p-3 sm:p-4 border border-slate-200">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Move className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-semibold text-slate-700">QR Position Controls</span>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">X Position</Label>
                  <Input
                    type="number"
                    value={qrPosition.x}
                    onChange={(e) => handleXChange(e.target.value)}
                    min="0"
                    max={PDF_DISPLAY_WIDTH - qrSize}
                    className="h-9 sm:h-10"
                    data-testid="qr-x-input"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Y Position</Label>
                  <Input
                    type="number"
                    value={qrPosition.y}
                    onChange={(e) => handleYChange(e.target.value)}
                    min="0"
                    max={PDF_DISPLAY_HEIGHT - qrSize}
                    className="h-9 sm:h-10"
                    data-testid="qr-y-input"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">QR Size</Label>
                  <div className="flex gap-1">
                    <Input
                      type="number"
                      value={qrSize}
                      onChange={(e) => handleSizeChange(e.target.value)}
                      min="5"
                      max="200"
                      className="h-9 sm:h-10 flex-1"
                      data-testid="qr-size-input"
                    />
                    <Button onClick={() => handleSizeChange(qrSize - 10)} variant="outline" size="sm" className="h-9 sm:h-10 w-9 sm:w-10 p-0">
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => handleSizeChange(qrSize + 10)} variant="outline" size="sm" className="h-9 sm:h-10 w-9 sm:w-10 p-0">
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600">Quick Position</Label>
                  <Select onValueChange={(val) => {
                    const positions = {
                      'top-left': { x: 20, y: 20 },
                      'top-right': { x: PDF_DISPLAY_WIDTH - qrSize - 20, y: 20 },
                      'bottom-left': { x: 20, y: PDF_DISPLAY_HEIGHT - qrSize - 20 },
                      'bottom-right': { x: PDF_DISPLAY_WIDTH - qrSize - 20, y: PDF_DISPLAY_HEIGHT - qrSize - 20 },
                      'center': { x: (PDF_DISPLAY_WIDTH - qrSize) / 2, y: (PDF_DISPLAY_HEIGHT - qrSize) / 2 }
                    };
                    if (positions[val]) {
                      setQrPosition({ x: Math.round(positions[val].x), y: Math.round(positions[val].y) });
                    }
                  }}>
                    <SelectTrigger className="h-9 sm:h-10">
                      <SelectValue placeholder="Preset" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="top-left">Top Left</SelectItem>
                      <SelectItem value="top-right">Top Right</SelectItem>
                      <SelectItem value="bottom-left">Bottom Left</SelectItem>
                      <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      <SelectItem value="center">Center</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <p className="text-xs text-slate-500 mt-3">Same position will be applied to all selected pages.</p>
            </div>

            {/* PDF Preview with Draggable QR */}
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 sm:p-4 bg-slate-100">
              <div 
                ref={containerRef}
                className="relative mx-auto bg-white shadow-lg"
                style={{ width: PDF_DISPLAY_WIDTH, height: PDF_DISPLAY_HEIGHT }}
              >
                {/* PDF Document */}
                {pdfFile && (
                  <Document
                    file={pdfFile}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <div className="absolute inset-0 flex items-center justify-center bg-white">
                        <div className="text-center">
                          <Loader2 className="w-8 h-8 text-slate-400 mx-auto mb-2 animate-spin" />
                          <p className="text-sm text-slate-600">Loading PDF...</p>
                        </div>
                      </div>
                    }
                    error={
                      <div className="absolute inset-0 flex items-center justify-center bg-white">
                        <div className="text-center text-red-600">
                          <FileText className="w-8 h-8 mx-auto mb-2" />
                          <p className="text-sm">Failed to load PDF</p>
                          <Button 
                            onClick={() => { setPdfLoading(true); setPdfError(false); }}
                            variant="outline" 
                            size="sm" 
                            className="mt-2"
                          >
                            Retry
                          </Button>
                        </div>
                      </div>
                    }
                  >
                    <Page 
                      key={`page-${currentPage}`}
                      pageNumber={currentPage} 
                      width={PDF_DISPLAY_WIDTH}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                )}
                
                {/* Draggable QR Code */}
                {!pdfLoading && !pdfError && isPageSelected && (
                  <div
                    ref={qrRef}
                    className={`absolute border-2 border-amber-600 bg-white rounded shadow-lg cursor-grab active:cursor-grabbing ${
                      isDragging ? 'ring-2 ring-amber-400 shadow-xl z-20' : 'z-10'
                    }`}
                    style={{ 
                      width: qrSize, 
                      height: qrSize,
                      left: qrPosition.x,
                      top: qrPosition.y,
                      touchAction: 'none'
                    }}
                    onMouseDown={handleDragStart}
                    onTouchStart={handleDragStart}
                    data-testid="draggable-qr"
                  >
                    <img
                      src={`data:image/png;base64,${qrCode}`}
                      alt="QR Code"
                      className="w-full h-full pointer-events-none select-none"
                      draggable="false"
                    />
                  </div>
                )}

                {/* Overlay for non-selected pages */}
                {!pdfLoading && !pdfError && !isPageSelected && (
                  <div className="absolute inset-0 bg-slate-900/20 flex items-center justify-center">
                    <div className="bg-white px-4 py-2 rounded-lg shadow text-sm text-slate-600">
                      QR not placed on this page
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button onClick={() => setStep(1)} variant="outline" className="flex-1 h-10 sm:h-11 order-2 sm:order-1">
                Back
              </Button>
              <Button
                onClick={handleProceedToMetadata}
                className="flex-1 h-10 sm:h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium order-1 sm:order-2"
                data-testid="proceed-to-metadata-button"
              >
                Proceed to Metadata
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Metadata Form */}
      {step === 3 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Document Metadata</h2>
              <p className="text-sm sm:text-base text-slate-600">Fill in the document details</p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <div className="space-y-2">
                <Label htmlFor="document-id" className="text-slate-700 font-medium">Document ID *</Label>
                <Input id="document-id" value={documentId} onChange={(e) => setDocumentId(e.target.value)} placeholder="DOC-XXXXX" className="h-10 sm:h-11" data-testid="document-id-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="document-name" className="text-slate-700 font-medium">Document Name *</Label>
                <Input id="document-name" value={documentName} onChange={(e) => setDocumentName(e.target.value)} placeholder="e.g., Plot Allotment Certificate" className="h-10 sm:h-11" data-testid="document-name-input" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="folder-name" className="text-slate-700 font-medium">Folder Name *</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  {folders.length > 0 && (
                    <Select value={folderName} onValueChange={setFolderName}>
                      <SelectTrigger className="h-10 sm:h-11 flex-1"><SelectValue placeholder="Select folder" /></SelectTrigger>
                      <SelectContent>
                        {folders.map((folder) => (<SelectItem key={folder} value={folder}>{folder}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  )}
                  <Input id="folder-name" value={folderName} onChange={(e) => setFolderName(e.target.value)} placeholder="Or type new folder" className="h-10 sm:h-11 flex-1" data-testid="folder-name-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="issue-date" className="text-slate-700 font-medium">Issue Date *</Label>
                  <Input id="issue-date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="h-10 sm:h-11" data-testid="issue-date-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="validity-status" className="text-slate-700 font-medium">Status *</Label>
                  <Select value={validityStatus} onValueChange={setValidityStatus}>
                    <SelectTrigger className="h-10 sm:h-11" data-testid="validity-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Valid">Valid</SelectItem>
                      <SelectItem value="Revoked">Revoked</SelectItem>
                      <SelectItem value="Invalid">Invalid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 sm:p-4 border border-slate-200">
              <p className="text-xs sm:text-sm text-slate-600"><strong>QR Position:</strong> X: {qrPosition.x}, Y: {qrPosition.y}, Size: {qrSize}px</p>
              <p className="text-xs sm:text-sm text-slate-600 mt-1">
                <strong>Pages:</strong> {selectedPages.length === numPages ? 'All pages' : `Page${selectedPages.length > 1 ? 's' : ''} ${selectedPages.join(', ')}`}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button onClick={() => setStep(2)} variant="outline" className="flex-1 h-10 sm:h-11 order-2 sm:order-1">Back</Button>
              <Button onClick={handleFinalize} disabled={finalizing} className="flex-1 h-10 sm:h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium order-1 sm:order-2" data-testid="generate-document-button">
                {finalizing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>) : 'Generate Document'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Complete */}
      {step === 4 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 lg:p-8">
          <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6 text-center">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">Document Generated!</h2>
            <p className="text-sm sm:text-base text-slate-600">
              QR code embedded on {selectedPages.length === numPages ? 'all pages' : `page${selectedPages.length > 1 ? 's' : ''} ${selectedPages.join(', ')}`}.
            </p>
            
            <div className="bg-slate-50 rounded-lg p-4 sm:p-6 text-left space-y-2 sm:space-y-3 text-sm">
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-slate-600">Document ID:</span>
                <span className="font-mono font-medium text-slate-900 truncate" data-testid="final-document-id">{documentId}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-slate-600">Verification ID:</span>
                <span className="font-mono text-xs text-slate-900 break-all" data-testid="final-verification-id">{verificationId}</span>
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                <span className="text-slate-600">Folder:</span>
                <span className="font-medium text-slate-900">{folderName}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button onClick={handleDownload} className="flex-1 h-10 sm:h-11 bg-amber-700 hover:bg-amber-800 text-white font-medium" data-testid="download-final-pdf-button">
                <Download className="w-5 h-5 mr-2" />Download
              </Button>
              <Button onClick={handleReset} className="flex-1 h-10 sm:h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium" data-testid="upload-another-button">
                Upload Another
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
